/**
 * Turning an uploaded picture into a logo the system can carry everywhere.
 *
 * The logo goes to every till with the settings, into the sign-in screen, and
 * onto every receipt. A photo straight off a phone is several megabytes; as a
 * logo it only ever needs to be a few hundred pixels across. So it is drawn
 * onto a canvas at most LOGO_PX on its longest side and saved small - PNG to
 * keep a transparent background, WebP when the PNG would still be heavy.
 */

const LOGO_PX = 480;
/** Comfortably under the server's limit, so a logo from here is never refused. */
const TARGET_CHARS = 240_000;

export class LogoError extends Error {
  constructor(readonly reason: 'not_image' | 'unreadable' | 'too_big') {
    super(reason);
  }
}

function load(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new LogoError('unreadable'));
    img.src = url;
  });
}

function draw(img: HTMLImageElement, max: number): HTMLCanvasElement {
  const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export async function logoFromFile(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) throw new LogoError('not_image');
  if (file.size > 20 * 1024 * 1024) throw new LogoError('too_big');
  const img = await load(file);
  try {
    for (const max of [LOGO_PX, 360, 240]) {
      const canvas = draw(img, max);
      const png = canvas.toDataURL('image/png');
      if (png.length <= TARGET_CHARS) return png;
      const webp = canvas.toDataURL('image/webp', 0.9);
      if (webp.startsWith('data:image/webp') && webp.length <= TARGET_CHARS) return webp;
    }
  } finally {
    URL.revokeObjectURL(img.src);
  }
  throw new LogoError('too_big');
}

// ---------------------------------------------------------------------------
// Product photos
// ---------------------------------------------------------------------------

/** Big enough for a product page on a large screen; a phone photo is 4000px. */
const PHOTO_PX = 1000;
/** A till tile is about 150px wide; twice that for a sharp screen. */
const THUMB_PX = 320;
/** The server takes 1.5 MB of photo, which is 2 million characters as base64. */
const PHOTO_CHARS = 1_900_000;

/** JPEG has no transparency: a see-through product shot would turn black. Paint white under it. */
function flat(img: HTMLImageElement, max: number): HTMLCanvasElement {
  const canvas = draw(img, max);
  const out = document.createElement('canvas');
  out.width = canvas.width;
  out.height = canvas.height;
  const ctx = out.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(canvas, 0, 0);
  return out;
}

/** A product photo and the small copy the till tiles use, both made here - never the phone's full file. */
export async function photoFromFile(file: File): Promise<{ photo: string; thumb: string }> {
  if (!file.type.startsWith('image/')) throw new LogoError('not_image');
  if (file.size > 40 * 1024 * 1024) throw new LogoError('too_big');
  const img = await load(file);
  try {
    let photo = '';
    for (const max of [PHOTO_PX, 800, 600]) {
      const c = flat(img, max);
      const webp = c.toDataURL('image/webp', 0.85);
      const out = webp.startsWith('data:image/webp') ? webp : c.toDataURL('image/jpeg', 0.85);
      if (out.length <= PHOTO_CHARS) {
        photo = out;
        break;
      }
    }
    if (!photo) throw new LogoError('too_big');
    return { photo, thumb: flat(img, THUMB_PX).toDataURL('image/jpeg', 0.8) };
  } finally {
    URL.revokeObjectURL(img.src);
  }
}

/** The small copy lives beside the photo under the same name. */
export const thumbOf = (url: string): string => url.replace(/\.[a-z]+$/, '-t.jpg');

let keeping = false;

/**
 * Fetch every tile photo once while there is internet, so the service worker
 * holds it for when there is not. Small copies only - a few hundred of them is
 * a few megabytes - one at a time, skipping any already kept, and stopping the
 * moment the connection goes.
 */
export async function keepPhotosForOffline(urls: (string | null | undefined)[]): Promise<void> {
  if (keeping || typeof caches === 'undefined' || !navigator.serviceWorker?.controller) return;
  keeping = true;
  try {
    const cache = await caches.open('photos');
    for (const u of [...new Set(urls.filter((x): x is string => !!x))].map(thumbOf)) {
      if (!navigator.onLine) break;
      if (await cache.match(u)) continue;
      await fetch(u).catch(() => undefined);
    }
  } catch {
    /* next refresh tries again */
  } finally {
    keeping = false;
  }
}
